import React, { useEffect, useRef, useState } from 'react';
import './VirtualProduction.css';
import { initWOW } from '../../utils/animations';

const VirtualProduction = () => {
  const carouselRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const videoRef = useRef(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  useEffect(() => {
    initWOW();
    document.body.classList.add('virtual-production-page-active');
    return () => {
      document.body.classList.remove('virtual-production-page-active');
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Set playback quality settings for smooth playback
      video.defaultPlaybackRate = 1.0;
      video.playbackRate = 1.0;
      
      // Increase buffer size for smoother playback
      if (video.buffered && video.buffered.length > 0) {
        // Wait for more data to buffer before playing
        video.addEventListener('progress', () => {
          if (video.buffered.length > 0) {
            const bufferedEnd = video.buffered.end(video.buffered.length - 1);
            const currentTime = video.currentTime;
            // If we have at least 3 seconds buffered ahead, we're good
            if (bufferedEnd - currentTime > 3 && video.paused) {
              video.play().catch(() => {});
            }
          }
        }, { once: false });
      }
      
      // Force load video with metadata
      video.load();
      
      // Function to play video
      const playVideo = async () => {
        try {
          if (video.paused) {
            await video.play();
          }
        } catch (error) {
          console.error('Error playing video:', error);
        }
      };
      
      // Handle waiting event (when video stalls)
      const handleWaiting = () => {
        // Pause briefly to let buffer catch up
        if (!video.paused) {
          video.pause();
          setTimeout(() => {
            if (video.readyState >= 3) {
              video.play().catch(() => {});
            }
          }, 100);
        }
      };
      
      // Handle playing event (when video resumes)
      const handlePlaying = () => {
        // Video is playing smoothly
      };
      
      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('playing', handlePlaying);
      
      // Try to play when video is ready
      const tryPlayWhenReady = () => {
        if (video.readyState >= 4) { // HAVE_ENOUGH_DATA
          playVideo();
          return true;
        }
        return false;
      };
      
      // Listen for events - wait for enough data
      const handleCanPlayThrough = () => {
        playVideo();
      };
      
      video.addEventListener('canplaythrough', handleCanPlayThrough, { once: true });
      
      // Try immediately if already loaded
      let timeoutId;
      if (!tryPlayWhenReady()) {
        // Also try after a delay
        timeoutId = setTimeout(playVideo, 500);
      }
      
      // Error handler
      video.addEventListener('error', (e) => {
        const error = video.error;
        if (error) {
          console.error('Video error:', error.code, error.message);
        }
      });
      
      return () => {
        // Cleanup
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('playing', handlePlaying);
        video.removeEventListener('canplaythrough', handleCanPlayThrough);
        if (video) {
          video.pause();
        }
      };
    }
  }, []);

  useEffect(() => {
    // Seamless infinite carousel with auto-scroll only
    const carousel = carouselRef.current;
    if (!carousel) return;

    // Set initial scroll position to start of original items (position 0)
    const setInitialPosition = () => {
      const cardWidth = carousel.querySelector('.screenshot-card')?.offsetWidth || 0;
      if (cardWidth > 0) {
        carousel.scrollLeft = 0; // Start at first original item
      }
    };

    // Wait for cards to render
    setTimeout(setInitialPosition, 100);

    // Seamless loop check - reset position when reaching end clones
    const checkLoop = () => {
      const cardWidth = carousel.querySelector('.screenshot-card')?.offsetWidth || 0;
      if (cardWidth === 0) return;
      
      const gap = 16;
      const cardWithGap = cardWidth + gap;
      const originalWidth = cardWithGap * 6; // 6 original items
      const endClonesStart = originalWidth; // Start of end clones
      
      const currentScroll = carousel.scrollLeft;
      
      // If scrolled to or past the end clones, reset to start (seamless)
      if (currentScroll >= endClonesStart - 1) {
        // Remove transition for instant jump
        carousel.style.scrollBehavior = 'auto';
        carousel.scrollLeft = currentScroll - originalWidth;
        
        // Restore smooth scrolling on next frame
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            carousel.style.scrollBehavior = 'smooth';
          });
        });
      }
    };

    // Auto-scroll function - moves one card every 3000ms
    const autoScroll = () => {
      const cardWidth = carousel.querySelector('.screenshot-card')?.offsetWidth || 0;
      if (cardWidth === 0) return;
      
      const gap = 16;
      const cardWithGap = cardWidth + gap;
      const scrollAmount = cardWithGap;
      
      // Always scroll one card to the left
      carousel.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
      
      // Check loop after scroll completes
      setTimeout(() => {
        checkLoop();
      }, 600);
    };

    // Start auto-scroll interval
    const startAutoScroll = () => {
      // Clear any existing interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
      }
      // Start new interval - scroll every 3000ms
      scrollIntervalRef.current = setInterval(autoScroll, 3000);
    };

    // Snap to nearest card
    const snapToCard = () => {
      const cardWidth = carousel.querySelector('.screenshot-card')?.offsetWidth || 0;
      if (cardWidth === 0) return;
      
      const gap = 16;
      const cardWithGap = cardWidth + gap;
      const currentScroll = carousel.scrollLeft;
      const snappedScroll = Math.round(currentScroll / cardWithGap) * cardWithGap;
      
      carousel.scrollTo({
        left: snappedScroll,
        behavior: 'smooth'
      });
    };

    const handleScroll = () => {
      checkLoop();
    };

    // Snap on scroll end (for auto-scroll)
    let scrollTimeout;
    const handleScrollEnd = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        snapToCard();
      }, 150);
    };

    // Add event listeners
    carousel.addEventListener('scroll', () => {
      handleScroll();
      handleScrollEnd();
    });

    // Start auto-scroll after initial setup
    setTimeout(startAutoScroll, 1500);

    // Cleanup function
    return () => {
      // Stop auto-scroll interval
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      clearTimeout(scrollTimeout);
      // Remove event listeners
      carousel.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const expertise = [
    {
      title: 'UE ENVIRONMENTS FOR LIVE SHOWS & BROADCAST',
      description: 'Custom real-time worlds built for stable performance and a strong, consistent on-air look.'
    },
    {
      title: 'REAL-TIME CINEMATICS & PREVIZ',
      description: 'Fast visual prototyping, shot exploration, and camera planning inside interactive UE environments.'
    },
    {
      title: 'REAL-TIME EVENT WORLDS',
      description: 'Immersive digital spaces for hybrid and virtual events, designed for reliability and quick iteration.'
    }
  ];

  const otherWorks = [
    {
      title: 'DESERT BACKDROP',
      description: 'Vast dunes and harsh sunlight, built with optimized materials and lighting for clean, stable real-time playback.',
      image: '/images/Desert_Fire.jpg',
      video: '/videos/Desert_Fire.webm'
    },
    {
      title: 'FOREST SET',
      description: 'Dense trees, ground detail, and atmospheric depth—crafted with scalable foliage and performance-friendly scene layering.',
      image: '/images/Forest.jpg',
      video: '/videos/forest.webm'
    },
    {
      title: 'FLOATING ISLANDS SCENE',
      description: 'Cinematic sky islands with strong silhouettes and depth—assembled for fast iteration, smooth camera moves, and real-time rendering.',
      image: '/images/Skyland.jpg',
      video: '/videos/skyland.webm'
    },
    {
      title: 'ABANDONED LANDSCAPE',
      description: 'A realistic outdoor scene with subtle decay and nature reclaiming space—set up for cinematic framing and stable on-set performance.',
      image: '/images/PostApocalypse.jpg',
      video: '/videos/postapocalypse.webm'
    }
  ];

  return (
    <div className="virtual-production-page">
      {/* 1. HERO SECTION */}
      <div id="virtual-production-hero" className="container-fluid pl-0 pr-0 clearfix" 
           style={{minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative', background: 'transparent'}}>
        <video 
          ref={videoRef}
          autoPlay 
          loop 
          muted 
          playsInline
          preload="auto"
          webkit-playsinline="true"
          onError={(e) => {
            const error = e.target.error;
            if (error) {
              console.error('Video error:', error.code, error.message);
            }
          }}
          onCanPlay={() => {
            const video = videoRef.current;
            if (video) {
              video.play().catch(err => console.error('Play error:', err));
            }
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
            backgroundColor: '#000',
            pointerEvents: 'none'
          }}
        >
          <source src={`${process.env.PUBLIC_URL || ''}/videos/VR_Production.webm`} type="video/webm" />
          <source src="/videos/VR_Production.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
        <div className="container-fluid position-relative px-5" style={{flex: 1, minHeight: 'calc(100vh - 80px)', zIndex: 1}}>
          <div className="position-absolute" style={{left: '3rem', bottom: '100px', maxWidth: '900px', zIndex: 2}}>
            <div className="wow bounceInRight">
              <h3 className="text-white text-uppercase mb-3">
                High-end Virtual Production Environments in Unreal Engine
              </h3>
              <p className="lead text-white mb-4 d-none d-md-block"></p>
              <a 
                href="#contact" 
                className="btn text-uppercase btn-lg wow bounceInUp btn-demo"
                style={{
                  backgroundColor: '#00a8ff',
                  borderColor: '#00a8ff',
                  color: '#fff',
                  borderRadius: 0
                }}
              >
                Discuss your project
              </a>
            </div>
          </div>
          <div className="position-absolute text-right" style={{right: '3rem', top: '100px', maxWidth: '780px', zIndex: 2}}>
            <div className="wow bounceInLeft">
              <p className="lead text-white text-uppercase mb-0">
                Real-time worlds for shows, broadcasts, and live events — built with precision, strong visual identity, and reliable on-set performance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 2. OUR EXPERTISE */}
      <div id="virtual-production-expertise" className="container-fluid fh5co-network">
        <div className="container-fluid px-5">
          <div className="row">
            <div className="col-12">
              <div className="quote-box2 wow bounceInDown" data-wow-delay=".15s">
                <h2 className="vp-gradient-text">OUR EXPERTISE</h2>
              </div>
            </div>
          </div>
          <div className="row align-items-stretch" style={{marginTop: '3.15rem'}}>
            {expertise.map((item, index) => (
              <div key={index} className="col-md-4">
                <div 
                  className={`card h-100 wow ${index === 0 ? 'bounceInLeft' : index === 1 ? 'bounceInUp' : 'bounceInRight'}`} 
                  data-wow-delay={`${0.1 + index * 0.05}s`}
                  style={{
                    borderRadius: 0,
                    position: 'relative',
                    overflow: 'hidden',
                    padding: 0,
                    backgroundColor: '#1a1a1a',
                    border: 'none'
                  }}
                >
                  <div className="card-body d-flex flex-column">
                    <h4 className="card-title text-white">{item.title}</h4>
                    <p className="card-text text-white">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 3. KARATE KOMBAT — FLAGSHIP CASE */}
      <div id="virtual-production-flagship" className="container-fluid fh5co-content-box" style={{backgroundColor: '#111111', paddingTop: '3.15rem', paddingBottom: '3.15rem'}}>
        <div className="container-fluid px-5" style={{paddingLeft: '45px', paddingRight: '41px'}}>
          <div className="row align-items-center">
            <div className="col-lg-6">
              <div className="video-container" style={{
                aspectRatio: '16/9',
                width: '100%',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/ELQncubqfT0"
                  title="Karate Kombat — Flagship Case"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                ></iframe>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="showcase-content">
                <h2 className="wow bounceInRight vp-gradient-text">
                  KARATE KOMBAT — FLAGSHIP CASE
                </h2>
                <p className="wow bounceInLeft text-white mb-3">
                  <strong>Task:</strong> Create a flexible visual environment that could evolve with the show, reduce physical production costs, and maintain a strong, consistent on-air identity.
                </p>
                <p className="wow bounceInLeft text-white mb-3">
                  <strong>What We Delivered:</strong> A fully real-time Unreal Engine arena replacing static backdrops — stylized, camera-ready, and stable for live broadcast.
                </p>
                <p className="wow bounceInLeft text-white mb-0">
                  <strong>Result:</strong> A scalable visual system that lowered production constraints, accelerated updates, and gave the show a distinctive, recognizable look.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. KARATE KOMBAT SCREENSHOTS CAROUSEL */}
      <div className="container-fluid fh5co-content-box" id="screenshots-carousel-wrapper" style={{backgroundColor: '#111111', paddingTop: '0', paddingBottom: '3.15rem'}}>
        <div className="container-fluid px-5" style={{paddingLeft: '45px', paddingRight: '41px'}}>
          <div className="screenshots-carousel-wrapper" style={{position: 'relative', marginTop: '0.7875rem'}}>
            <div ref={carouselRef} className="screenshots-carousel" style={{
              display: 'flex',
              gap: '1rem',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollBehavior: 'smooth',
              paddingBottom: '1rem',
              userSelect: 'none',
              pointerEvents: 'none'
            }}>
              {/* Original items */}
              {[
                { num: 1, image: '/images/7.jpg' },
                { num: 2, image: '/images/KC_Okinawa_Promo_Rendering05_2021-03-16.png' },
                { num: 3, image: '/images/KC_Okinawa_Promo_Rendering09_2021-03-16.png' },
                { num: 4, image: '/images/KC_Env02_PromoRendering07_2021-07-14.png' },
                { num: 5, image: '/images/Preview_screenshot.00001.png' },
                { num: 6, image: '/images/Preview_screenshot.00046.png' }
              ].map((item) => (
                <div 
                  key={item.num}
                  className="screenshot-card"
                  style={{
                    backgroundImage: `url('${item.image}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                </div>
              ))}
              {/* Duplicate first 3 items at the end for seamless loop */}
              {[
                { num: 1, image: '/images/7.jpg' },
                { num: 2, image: '/images/KC_Okinawa_Promo_Rendering05_2021-03-16.png' },
                { num: 3, image: '/images/KC_Okinawa_Promo_Rendering09_2021-03-16.png' }
              ].map((item) => (
                <div 
                  key={`clone-end-${item.num}`}
                  className="screenshot-card"
                  style={{
                    backgroundImage: `url('${item.image}')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                >
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* 5. OTHER WORKS */}
      <div className="container-fluid fh5co-content-box" id="virtual-production-works-wrapper" style={{backgroundColor: '#111111'}}>
        <div className="container-fluid px-5">
          <div id="virtual-production-works">
            <div className="row">
              <div className="col-12">
                <div className="quote-box2 wow bounceInDown" data-wow-delay=".15s">
                  <h2 className="vp-gradient-text">ENVIRONMENT SHOWCASE</h2>
                </div>
              </div>
            </div>
            {/* First row with 2 cards */}
            <div className="row align-items-start">
              {otherWorks.slice(0, 2).map((work, index) => {
                const workIndex = index; // 0 or 1
                return (
                <div key={`work-${workIndex}`} className="col-lg-6 d-flex flex-column">
                  {index === 0 ? (
                    <>
                      <div 
                        className="vp-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.1 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                        onMouseEnter={() => setHoveredIndex(workIndex)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        {hoveredIndex === workIndex && work.video ? (
                          <video
                            key={`video-${workIndex}`}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="vp-work-media w-100 h-100"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={work.video} type="video/webm" />
                          </video>
                        ) : (
                        <div 
                          className="vp-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                              height: '100%',
                              backgroundImage: work.image ? `url('${work.image}')` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }}
                          ></div>
                        )}
                        <div 
                          className="position-absolute w-100 h-100" 
                          style={{
                            bottom: 0,
                            left: 0,
                            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        ></div>
                        <div className="position-absolute" style={{bottom: 0, left: '40px', right: '40px', paddingBottom: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                      <div className="vp-work-text-wrapper">
                        <div className="vp-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="vp-work-text-wrapper">
                        <div className="vp-work-text-content">
                          <p className="mb-0">{work.description}</p>
                        </div>
                      </div>
                      <div 
                        className="vp-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.1 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                        onMouseEnter={() => setHoveredIndex(workIndex)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        {hoveredIndex === workIndex && work.video ? (
                          <video
                            key={`video-${workIndex}`}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="vp-work-media w-100 h-100"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={work.video} type="video/webm" />
                          </video>
                        ) : (
                        <div 
                          className="vp-work-media w-100 h-100" 
                          style={{
                            backgroundColor: '#000',
                            width: '100%',
                              height: '100%',
                              backgroundImage: work.image ? `url('${work.image}')` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }}
                          ></div>
                        )}
                        <div 
                          className="position-absolute w-100 h-100" 
                          style={{
                            top: 0,
                            left: 0,
                            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        ></div>
                        <div className="position-absolute" style={{top: 0, left: '40px', right: '40px', paddingTop: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                );
              })}
            </div>
            {/* Second row with 2 cards */}
            <div className="row align-items-start" style={{marginTop: '1rem'}}>
              {[otherWorks[3], otherWorks[2]].map((work, index) => {
                // Calculate original index: [3, 2] -> index 0 = original 3, index 1 = original 2
                const workIndex = index === 0 ? 3 : 2;
                return (
                <div key={`work-${workIndex}`} className="col-lg-6 d-flex flex-column">
                  {index === 0 ? (
                    <>
                  <div 
                    className="vp-work-item position-relative wow bounceInUp" 
                    data-wow-delay={`${0.3 + index * 0.05}s`}
                    style={{overflow: 'hidden'}}
                        onMouseEnter={() => setHoveredIndex(workIndex)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        {hoveredIndex === workIndex && work.video ? (
                          <video
                            key={`video-${workIndex}`}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="vp-work-media w-100 h-100"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={work.video} type="video/webm" />
                          </video>
                        ) : (
                    <div 
                      className="vp-work-media w-100 h-100" 
                      style={{
                        backgroundColor: '#000',
                        width: '100%',
                              height: '100%',
                              backgroundImage: work.image ? `url('${work.image}')` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }}
                          ></div>
                        )}
                        <div 
                          className="position-absolute w-100 h-100" 
                          style={{
                            bottom: 0,
                            left: 0,
                            background: 'linear-gradient(to top, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                            pointerEvents: 'none',
                            zIndex: 1
                      }}
                    ></div>
                        <div className="position-absolute" style={{bottom: 0, left: '40px', right: '40px', paddingBottom: '30px', zIndex: 2}}>
                      <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                    </div>
                  </div>
                  <div className="vp-work-text-wrapper">
                    <div className="vp-work-text-content">
                      <p className="mb-0">{work.description}</p>
                    </div>
                  </div>
                    </>
                  ) : (
                    <>
                      <div className="vp-work-text-wrapper">
                        <div className="vp-work-text-content">
                          <p className="mb-0">{work.description}</p>
            </div>
          </div>
                      <div 
                        className="vp-work-item position-relative wow bounceInUp" 
                        data-wow-delay={`${0.3 + index * 0.05}s`}
                        style={{overflow: 'hidden'}}
                        onMouseEnter={() => setHoveredIndex(workIndex)}
                        onMouseLeave={() => setHoveredIndex(null)}
                      >
                        {hoveredIndex === workIndex && work.video ? (
                          <video
                            key={`video-${workIndex}`}
                            autoPlay
                            loop
                            muted
                            playsInline
                            className="vp-work-media w-100 h-100"
                style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                              backgroundColor: '#000'
                            }}
                          >
                            <source src={work.video} type="video/webm" />
                          </video>
                        ) : (
                          <div 
                            className="vp-work-media w-100 h-100" 
                            style={{
                              backgroundColor: '#000',
                              width: '100%',
                              height: '100%',
                              backgroundImage: work.image ? `url('${work.image}')` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat'
                            }}
                          ></div>
                        )}
                        <div 
                          className="position-absolute w-100 h-100" 
                          style={{
                            top: 0,
                            left: 0,
                            background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.6) 0%, transparent 40%)',
                            pointerEvents: 'none',
                            zIndex: 1
                          }}
                        ></div>
                        <div className="position-absolute" style={{top: 0, left: '40px', right: '40px', paddingTop: '30px', zIndex: 2}}>
                          <h3 className="text-white mb-0" style={{margin: 0, padding: 0, lineHeight: '1.2'}}>{work.title}</h3>
                        </div>
                      </div>
                    </>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VirtualProduction;
